/**
 * Media Service
 * Manages multiple media providers with fallback logic
 */

import { MediaProvider, MediaSearchResponse, MediaPhoto, MediaVideo } from './mediaProvider';
import { pexelsAPI } from './pexelsApi';
import { pixabayAPI } from './pixabayApi';
import { unsplashAPI } from './unsplashApi';
import { coverrAPI } from './coverrApi';
import { freepikAPI } from './freepikApi';
import { serverProxyAPI } from './serverProxyApi';

export class MediaService {
    private providers: MediaProvider[] = [];

    constructor() {
        // Register providers in order of preference
        // Unsplash first for better semantic search on images
        if (unsplashAPI) {
            this.providers.push(unsplashAPI);
        }
        if (pexelsAPI) {
            this.providers.push(pexelsAPI);
        }
        if (pixabayAPI) {
            this.providers.push(pixabayAPI);
        }
        // Coverr for high-quality cinematic videos (free, allows hotlinking)
        if (coverrAPI) {
            this.providers.push(coverrAPI);
        }
        // Freepik/Videvo for additional video content (requires download to GCS)
        if (freepikAPI) {
            this.providers.push(freepikAPI);
        }

        // Fallback: if no direct API keys configured, use the server proxy
        // The api-server has PEXELS_API_KEY in SSM and proxies requests.
        if (this.providers.length === 0) {
            this.providers.push(serverProxyAPI);
            console.log('✅ MediaService using server proxy (no direct API keys configured)');
        } else {
            console.log(`✅ MediaService initialized with ${this.providers.length} provider(s): ${this.providers.map(p => p.name).join(', ')}`);
        }
    }

    /**
     * Execute a function with fallback to subsequent providers if the first fails
     */
    private async executeWithFallback<T>(
        action: (provider: MediaProvider) => Promise<T>,
        actionName: string
    ): Promise<T> {
        let lastError: any;

        for (const provider of this.providers) {
            try {
                // console.log(`Attempting ${actionName} with ${provider.name}...`);
                return await action(provider);
            } catch (error) {
                console.warn(`❌ ${provider.name} ${actionName} failed:`, error);
                lastError = error;
                // Continue to next provider
            }
        }

        throw lastError || new Error(`All providers failed for ${actionName}`);
    }

    async searchPhotos(
        query: string,
        page = 1,
        perPage = 20
    ): Promise<MediaSearchResponse<MediaPhoto>> {
        return this.executeWithFallback(
            (provider) => provider.searchPhotos(query, page, perPage),
            'searchPhotos'
        );
    }

    async searchVideos(
        query: string,
        page = 1,
        perPage = 20
    ): Promise<MediaSearchResponse<MediaVideo>> {
        return this.executeWithFallback(
            (provider) => provider.searchVideos(query, page, perPage),
            'searchVideos'
        );
    }

    async getCuratedPhotos(
        page = 1,
        perPage = 20
    ): Promise<MediaSearchResponse<MediaPhoto>> {
        return this.executeWithFallback(
            (provider) => provider.getCuratedPhotos(page, perPage),
            'getCuratedPhotos'
        );
    }

    async getPopularVideos(
        page = 1,
        perPage = 20
    ): Promise<MediaSearchResponse<MediaVideo>> {
        return this.executeWithFallback(
            (provider) => provider.getPopularVideos(page, perPage),
            'getPopularVideos'
        );
    }

    /**
     * Get list of available providers with their capabilities
     */
    getAvailableProviders(): Array<{
        name: string;
        supportsImages: boolean;
        supportsVideos: boolean;
        allowsHotlinking: boolean;
    }> {
        return this.providers.map(provider => ({
            name: provider.name,
            // Coverr and Freepik are video-only providers
            supportsImages: provider.name !== 'coverr' && provider.name !== 'freepik',
            // Unsplash doesn't support videos
            supportsVideos: provider.name !== 'unsplash',
            // Freepik doesn't allow hotlinking - videos must be downloaded to GCS
            allowsHotlinking: provider.name !== 'freepik'
        }));
    }

    /**
     * Search photos from a specific provider
     */
    async searchPhotosFromProvider(
        providerName: string,
        query: string,
        page = 1,
        perPage = 20
    ): Promise<MediaSearchResponse<MediaPhoto>> {
        const provider = this.providers.find(p => p.name === providerName);
        if (!provider) {
            throw new Error(`Provider "${providerName}" not found or not configured`);
        }
        return provider.searchPhotos(query, page, perPage);
    }

    /**
     * Search videos from a specific provider
     *
     * Note: If using 'freepik' provider, videos cannot be hotlinked and must be
     * downloaded to GCS before use in slides.
     */
    async searchVideosFromProvider(
        providerName: string,
        query: string,
        page = 1,
        perPage = 20
    ): Promise<MediaSearchResponse<MediaVideo>> {
        const provider = this.providers.find(p => p.name === providerName);
        if (!provider) {
            throw new Error(`Provider "${providerName}" not found or not configured`);
        }
        if (provider.name === 'unsplash') {
            throw new Error('Unsplash does not support video search');
        }
        return provider.searchVideos(query, page, perPage);
    }

    /**
     * Get curated photos from a specific provider
     */
    async getCuratedPhotosFromProvider(
        providerName: string,
        page = 1,
        perPage = 20
    ): Promise<MediaSearchResponse<MediaPhoto>> {
        const provider = this.providers.find(p => p.name === providerName);
        if (!provider) {
            throw new Error(`Provider "${providerName}" not found or not configured`);
        }
        return provider.getCuratedPhotos(page, perPage);
    }

    /**
     * Get popular videos from a specific provider
     */
    async getPopularVideosFromProvider(
        providerName: string,
        page = 1,
        perPage = 20
    ): Promise<MediaSearchResponse<MediaVideo>> {
        const provider = this.providers.find(p => p.name === providerName);
        if (!provider) {
            throw new Error(`Provider "${providerName}" not found or not configured`);
        }
        if (provider.name === 'unsplash') {
            throw new Error('Unsplash does not support video search');
        }
        return provider.getPopularVideos(page, perPage);
    }

    /**
     * Search photos from ALL providers in parallel and combine results
     */
    async searchPhotosFromAllProviders(
        query: string,
        page = 1,
        perPage = 15
    ): Promise<MediaSearchResponse<MediaPhoto>> {
        const results = await Promise.allSettled(
            this.providers.map(provider =>
                provider.searchPhotos(query, page, perPage)
            )
        );

        const allPhotos: MediaPhoto[] = [];
        let totalResults = 0;
        let hasNextPage = false;

        results.forEach((result, index) => {
            if (result.status === 'fulfilled' && result.value.photos) {
                allPhotos.push(...result.value.photos);
                totalResults += result.value.total_results || 0;
                if (result.value.next_page) hasNextPage = true;
            } else if (result.status === 'rejected') {
                console.warn(`❌ ${this.providers[index]?.name} searchPhotos failed:`, result.reason);
            }
        });

        // Shuffle results to mix providers
        const shuffled = allPhotos.sort(() => Math.random() - 0.5);

        return {
            page,
            per_page: perPage * this.providers.length,
            total_results: totalResults,
            next_page: hasNextPage ? `page=${page + 1}` : undefined,
            photos: shuffled
        };
    }

    /**
     * Search videos from ALL providers (except Unsplash) in parallel and combine results
     *
     * Note: Freepik videos cannot be hotlinked. When selecting a Freepik video,
     * you must download it to GCS before using in slides. Check video.source === 'freepik'
     * to identify videos that need downloading.
     */
    async searchVideosFromAllProviders(
        query: string,
        page = 1,
        perPage = 20
    ): Promise<MediaSearchResponse<MediaVideo>> {
        // Filter out Unsplash as it doesn't support videos
        const videoProviders = this.providers.filter(p => p.name !== 'unsplash');

        if (videoProviders.length === 0) {
            return {
                page,
                per_page: perPage,
                total_results: 0,
                videos: []
            };
        }

        const results = await Promise.allSettled(
            videoProviders.map(provider =>
                provider.searchVideos(query, page, perPage)
            )
        );

        const allVideos: MediaVideo[] = [];
        let totalResults = 0;
        let hasNextPage = false;

        results.forEach((result, index) => {
            if (result.status === 'fulfilled' && result.value.videos) {
                allVideos.push(...result.value.videos);
                totalResults += result.value.total_results || 0;
                if (result.value.next_page) hasNextPage = true;
            } else if (result.status === 'rejected') {
                console.warn(`❌ ${videoProviders[index]?.name} searchVideos failed:`, result.reason);
            }
        });

        // Shuffle results to mix providers
        const shuffled = allVideos.sort(() => Math.random() - 0.5);

        return {
            page,
            per_page: perPage * videoProviders.length,
            total_results: totalResults,
            next_page: hasNextPage ? `page=${page + 1}` : undefined,
            videos: shuffled
        };
    }
}

export const mediaService = new MediaService();
